import { isEqual, findIndex } from 'lodash';
import moment from 'moment';
import { roundTimeDown } from '../../../utils/time';
import * as types from './types';

import createMetadataAPIReducer from '../../utils/api-metadata-reducer';
const defaultReducer = createMetadataAPIReducer({ baseType: 'DASHBOARDS' });

const initialState = {
  currentDashboardId: null,
  // true if widgets are being shown on a dashboard, false if they are being
  // used standalone. This is needed to determine whether we should use the
  // dashboard's access scope token, or the user's token for API calls in the
  // widgets
  dashboardMode: false,
  editMode: false,
  locked: false,
  byId: {},
  ids: [],
  lastError: null,
  error: null,
  lastFetch: null,
  loading: false,
  dashboardDatetime: {
    // dateTime: the point in time for which info should be shown on the dashboard
    dateTime: roundTimeDown().format(),
    // locked: false iff tracking NOW, otherwise set and locked to specific time
    locked: false
  },
  activePageIndex: 0,
  // If available, this token gives access to regional information, instead of
  // defaulting to the scope of information available to the current user. This
  // is needed to show regional widgets, such as the ContactListWidget for roles
  // throughout the region.
  dashboardReadAuthToken: undefined,
  // If in incident mode, the currently displayed incident id is captured in the state,
  // which will be used for all incident mode widgets
  displayedIncidentId: null
};

const updateDashboard = (state, dashboard) => {
  return {
    ...state,
    byId: {
      ...state.byId,
      [dashboard.id]: dashboard
    }
  };
};

// add widget to page with pageIndex
const addWidget = (state, widget, pageIndex = state.activePageIndex) => {
  const currentDashboard = state.byId[state.currentDashboardId];
  if (!currentDashboard) return state;

  const newDashboard = {
    ...currentDashboard,
    pages: currentDashboard.pages.map((page, index) =>
      index === pageIndex ? { ...page, widgets: [...page.widgets, widget] } : page
    )
  };

  return updateDashboard(state, newDashboard);
};

// remove widget with ID
const removeWidget = (state, widgetId) => {
  const currentDashboard = state.byId[state.currentDashboardId];
  if (!currentDashboard) return state;

  // find page on which widget is present
  const pageIndex = currentDashboard.pages.findIndex(page =>
    page.widgets.find(widget => String(widget.id) === String(widgetId))
  );

  // return state if widget with ID does not exist
  if (pageIndex === -1) return state;

  let { widgets } = currentDashboard.pages[pageIndex];
  widgets = widgets.filter(widget => String(widget.id) !== String(widgetId));

  // return new state with widget filtered out
  const newDashboard = {
    ...currentDashboard,
    pages: currentDashboard.pages.map((page, index) => (index === pageIndex ? { ...page, widgets } : page))
  };

  return updateDashboard(state, newDashboard);
};

const updateWidgets = (state, widgets, pageIndex) => {
  const currentDashboard = state.byId[state.currentDashboardId];
  if (!currentDashboard) return state;

  const oldWidgets = currentDashboard.pages[pageIndex].widgets;
  if (isEqual(oldWidgets, widgets)) return state;

  const newWidgets = widgets.map(newWidget => {
    const oldWidget = oldWidgets.find(ow => ow.id === newWidget.id);
    return isEqual(oldWidget, newWidget) ? oldWidget : newWidget;
  });

  const newDashboard = {
    ...currentDashboard,
    pages: currentDashboard.pages.map((page, index) => (index === pageIndex ? { ...page, widgets: newWidgets } : page))
  };

  return updateDashboard(state, newDashboard);
};

// returns the page on which the widget appears
const findWidgetPage = (state, widgetId) => {
  const currentDashboard = state.byId[state.currentDashboardId];
  if (!currentDashboard) return null;

  // find page on which widget is present
  const pageIndex = currentDashboard.pages.findIndex(page =>
    page.widgets.find(widget => String(widget.id) === String(widgetId))
  );

  // return null if widget with ID does not exist
  if (pageIndex === -1) return null;

  return currentDashboard.pages[pageIndex];
};

const findWidget = (state, widgetId) => {
  const page = findWidgetPage(state, widgetId);
  if (!page) return null;

  return page.widgets.find(w => String(w.id) === String(widgetId));
};

const updateWidget = (state, widgetId, fields) => {
  const currentDashboard = state.byId[state.currentDashboardId];
  if (!currentDashboard) return state;

  // find page on which widget is present
  const pageIndex = currentDashboard.pages.findIndex(page =>
    page.widgets.find(widget => String(widget.id) === String(widgetId))
  );

  // return state if widget with ID does not exist
  if (pageIndex === -1) return state;

  const { widgets } = currentDashboard.pages[pageIndex];
  const newWidgets = widgets.map(widget =>
    String(widget.id) === String(widgetId) ? { ...widget, ...fields } : widget
  );

  if (isEqual(widgets, newWidgets)) return state;

  // return new state with widget updated
  const newDashboard = {
    ...currentDashboard,
    pages: currentDashboard.pages.map((page, index) => (index === pageIndex ? { ...page, widgets: newWidgets } : page))
  };

  return updateDashboard(state, newDashboard);
};

const updateWidgetSettings = (state, widgetId, fields) => {
  const widget = findWidget(state, widgetId);
  if (!widget) return state;

  const settings = {
    ...widget.settings,
    ...fields
  };

  const newFields = { settings: settings };

  return updateWidget(state, widgetId, newFields);
};

const activatePage = (state, pageType) => {
  const currentDashboard = state.byId[state.currentDashboardId];
  if (!currentDashboard) return state;

  const incidentPageIndex = findIndex(currentDashboard.pages, ['page_type', pageType]);
  if (incidentPageIndex === -1 || incidentPageIndex === state.activePageIndex) {
    return state;
  }
  return {
    ...state,
    activePageIndex: incidentPageIndex
  };
};

export default (state = initialState, action) => {
  state = defaultReducer(state, action);

  switch (action.type) {
    case types.SET_CURRENT_DASHBOARD: {
      return {
        ...state,
        currentDashboardId: action.payload.id
      };
    }
    case types.WIDGETS_UPDATED: {
      return updateWidgets(state, action.payload.widgets, action.payload.pageIndex);
    }
    case types.WIDGET_UPDATED: {
      return updateWidget(state, action.payload.widgetId, action.payload.fields);
    }
    case types.WIDGET_SETTINGS_UPDATED: {
      return updateWidgetSettings(state, action.payload.widgetId, action.payload.fields);
    }
    case types.WIDGET_REMOVED: {
      return removeWidget(state, action.payload.widgetId);
    }
    case types.WIDGET_ADDED: {
      return addWidget(state, action.payload.widget, action.payload.pageIndex);
    }
    // Set active index as the first incident page.
    case types.ACTIVATE_INCIDENT_MODE: {
      return activatePage(state, 'incident');
    }
    // Set active index as the first normal page
    case types.ACTIVATE_NORMAL_MODE: {
      return activatePage(state, 'normal');
    }
    case types.DASHBOARD_AUTH_TOKEN_RECEIVED: {
      return {
        ...state,
        dashboardReadAuthToken: action.payload.token
      };
    }
    // Set the dashboard to a specific time. This locks the time to the
    // specified one, and it will stop tracking NOW, unless unlocked
    case types.SET_DASHBOARD_DATETIME: {
      return {
        ...state,
        dashboardDatetime: {
          dateTime: moment(action.payload.dateTime).format(),
          locked: true
        }
      };
    }
    case types.SET_DASHBOARD_MODE: {
      return {
        ...state,
        dashboardMode: action.payload.mode
      };
    }
    // Set the dashboard time to track NOW, which means that it will move to the
    // next schedule interval every 15minutes. This means that the dashboard
    // shows real time information
    case types.UNLOCK_DASHBOARD_DATETIME: {
      return {
        ...state,
        dashboardDatetime: {
          dateTime: roundTimeDown().format(),
          locked: false
        }
      };
    }
    // A new schedule increment has been received. If dashboard time is
    // unlocked, the time will progress with 15 minutes and therefore move to
    // the next interval
    case 'SCHEDULE_TICK': {
      if (state.dashboardDatetime.locked) return state;
      return {
        ...state,
        dashboardDatetime: {
          dateTime: roundTimeDown().format(),
          locked: false
        }
      };
    }
    case types.UPDATE_DISPLAYED_INCIDENT_ID: {
      // If the dashboard is currently locked, and we're not forcing
      // the change, we don't update the displayed incident. This avoids
      // a situation where the dashboard is locked, a new incident comes
      // and then dashboard changes to the new incident, while remaining
      // locked.
      if (state.locked && !action.payload.force) return state;
      return {
        ...state,
        displayedIncidentId: action.payload.id
      };
    }
    case types.UPDATE_EDIT_MODE: {
      return {
        ...state,
        editMode: action.payload.editMode
      };
    }
    case types.LOCK_INCIDENT: {
      return {
        ...state,
        locked: action.payload.lockValue
      };
    }
    default: {
      return state;
    }
  }
};
